import React, { useState } from 'react';
import { ChevronDown, ChevronRight, FileText, Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ExternalLink } from '@/components/ui/external-link';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { formatDate, formatStripeAmount } from '../../utils/formatStripe';
import type { StripeInvoiceInfo } from '../../types/api';
import InvoicePdfView from './InvoicePdfView';

type BadgeVariant = 'default' | 'secondary' | 'destructive' | 'outline';

const STATUS: Record<string, { label: string; variant: BadgeVariant }> = {
  paid: { label: 'Paid', variant: 'default' },
  open: { label: 'Open', variant: 'secondary' },
  draft: { label: 'Draft', variant: 'outline' },
  uncollectible: { label: 'Uncollectible', variant: 'destructive' },
  void: { label: 'Void', variant: 'outline' },
};

/** How an overdue invoice renders — Stripe has no status of its own for it. */
const OVERDUE: { label: string; variant: BadgeVariant } = {
  label: 'Past',
  variant: 'destructive',
};

/**
 * Whether an invoice is past its due date.
 *
 * Stripe's status enum is draft|open|paid|uncollectible|void, so a late
 * invoice still reports `open` and would otherwise render as unremarkable.
 * `due_date` is null for charge_automatically invoices, which reads as not
 * overdue — correctly, since Stripe collects those itself.
 */
export function isOverdue(
  inv: Pick<StripeInvoiceInfo, 'status' | 'due_date'>,
  now: Date = new Date(),
): boolean {
  if (inv.status !== 'open' || !inv.due_date) return false;
  const due = new Date(inv.due_date);
  if (Number.isNaN(due.getTime())) return false;
  return due.getTime() < now.getTime();
}

interface InvoiceSummaryListProps {
  invoices: StripeInvoiceInfo[];
  /** More history exists; renders a "Load more" button. */
  hasMore?: boolean;
  /** Fetches the next page. Resolves when it has been appended. */
  onLoadMore?: () => Promise<void> | void;
  /** Denser layout for the HubSpot modal, which has far less width. */
  compact?: boolean;
}

/**
 * Stripe invoices, each expanding to its line items, discounts and taxes.
 *
 * Every figure here is rendered straight from the API. Stripe computes all of
 * them across all line items — amount_remaining, the subtotals, and the
 * pre-aggregated total_taxes / total_discount_amounts — so this component does
 * no arithmetic. That also means the footer stays correct no matter how many
 * lines are expanded, and regardless of how many the API returned.
 */
const InvoiceSummaryList: React.FC<InvoiceSummaryListProps> = ({
  invoices,
  hasMore = false,
  onLoadMore,
  compact = false,
}) => {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [pdfInvoice, setPdfInvoice] = useState<StripeInvoiceInfo | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);

  const handleLoadMore = async () => {
    if (!onLoadMore || loadingMore) return;
    setLoadingMore(true);
    try {
      await onLoadMore();
    } finally {
      setLoadingMore(false);
    }
  };

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  if (invoices.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">No invoices yet.</p>
    );
  }

  return (
    <div className="space-y-2">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-8" />
            <TableHead>Invoice</TableHead>
            <TableHead>Status</TableHead>
            {!compact && <TableHead>Date</TableHead>}
            <TableHead className="text-right">Due</TableHead>
            <TableHead className="text-right">Outstanding</TableHead>
            <TableHead className="w-24" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {invoices.map((inv) => {
            const isOpen = expanded.has(inv.id);
            // Overdue outranks the plain status, which is still "open".
            const p = isOverdue(inv)
              ? OVERDUE
              : STATUS[inv.status ?? ''] ?? {
                  label: inv.status ?? 'Unknown',
                  variant: 'outline' as BadgeVariant,
                };
            const colSpan = compact ? 6 : 7;
            return (
              <React.Fragment key={inv.id}>
                <TableRow
                  className="cursor-pointer"
                  onClick={() => toggle(inv.id)}
                >
                  <TableCell>
                    {isOpen ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                  </TableCell>
                  <TableCell className="font-medium">
                    {inv.number ?? inv.id}
                  </TableCell>
                  <TableCell>
                    <Badge variant={p.variant}>{p.label}</Badge>
                  </TableCell>
                  {!compact && <TableCell>{formatDate(inv.created)}</TableCell>}
                  <TableCell className="text-right">
                    {formatStripeAmount(inv.amount_due, inv.currency)}
                  </TableCell>
                  <TableCell className="text-right">
                    {formatStripeAmount(inv.amount_remaining, inv.currency)}
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-1">
                      {inv.pdf_url && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setPdfInvoice(inv)}
                          aria-label={`View PDF for invoice ${inv.number ?? inv.id}`}
                        >
                          <FileText className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      {inv.hosted_invoice_url && (
                        <ExternalLink
                          href={inv.hosted_invoice_url}
                          className="text-xs"
                        >
                          Stripe
                        </ExternalLink>
                      )}
                    </div>
                  </TableCell>
                </TableRow>

                {isOpen && (
                  <TableRow className="hover:bg-transparent">
                    <TableCell colSpan={colSpan} className="bg-muted/40 p-4">
                      <InvoiceDetail invoice={inv} />
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>

      {hasMore && onLoadMore && (
        <div className="flex justify-center pt-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleLoadMore}
            disabled={loadingMore}
          >
            {loadingMore ? (
              <>
                <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                Loading…
              </>
            ) : (
              'Load more invoices'
            )}
          </Button>
        </div>
      )}

      <Dialog
        open={pdfInvoice !== null}
        onOpenChange={(open) => !open && setPdfInvoice(null)}
      >
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>
              Invoice {pdfInvoice?.number ?? pdfInvoice?.id}
            </DialogTitle>
          </DialogHeader>
          {pdfInvoice && <InvoicePdfView invoiceId={pdfInvoice.id} />}
          {pdfInvoice?.hosted_invoice_url && (
            <ExternalLink href={pdfInvoice.hosted_invoice_url} className="text-sm">
              Open invoice in Stripe
            </ExternalLink>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

/** One invoice's line items, then its discounts, taxes and totals. */
const InvoiceDetail: React.FC<{ invoice: StripeInvoiceInfo }> = ({ invoice }) => {
  const lines = invoice.lines ?? [];
  const taxes = invoice.total_taxes ?? [];
  const discounts = invoice.total_discount_amounts ?? [];
  const currency = invoice.currency;

  return (
    <div className="space-y-4 text-sm">
      {lines.length > 0 ? (
        <div>
          <div className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Line items
          </div>
          <div className="space-y-1">
            {lines.map((line, i) => (
              <div
                key={line.id ?? i}
                className="flex items-baseline justify-between gap-4"
              >
                <div className="min-w-0">
                  <div className="truncate">
                    {line.description ?? line.product_id ?? 'Line item'}
                    {line.proration && (
                      <Badge variant="outline" className="ml-2 text-[10px]">
                        Proration
                      </Badge>
                    )}
                  </div>
                  {(line.quantity != null || line.period_start) && (
                    <div className="text-xs text-muted-foreground">
                      {line.quantity != null && <>Qty {line.quantity}</>}
                      {line.quantity != null && line.period_start && ' · '}
                      {line.period_start && (
                        <>
                          {formatDate(line.period_start)} –{' '}
                          {formatDate(line.period_end)}
                        </>
                      )}
                    </div>
                  )}
                </div>
                <div className="shrink-0 tabular-nums">
                  {formatStripeAmount(line.amount, line.currency ?? currency)}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">
          No line detail available for this invoice.
        </p>
      )}

      <div className="space-y-1 border-t pt-2">
        <TotalRow
          label="Subtotal"
          value={formatStripeAmount(invoice.subtotal, currency)}
        />

        {discounts.map((d, i) => (
          <TotalRow
            key={`discount-${i}`}
            // Numbered rather than labelled with the raw di_... id, which means
            // nothing to a reader; the coupon name isn't on this payload.
            label={discounts.length > 1 ? `Discount ${i + 1}` : 'Discount'}
            value={`−${formatStripeAmount(d.amount, currency)}`}
          />
        ))}

        {taxes.map((t, i) => (
          <TotalRow
            key={`tax-${i}`}
            // Inclusive tax is already inside the total; saying so stops it
            // reading as an extra charge on top.
            label={t.tax_behavior === 'inclusive' ? 'Tax (included)' : 'Tax'}
            value={formatStripeAmount(t.amount, currency)}
          />
        ))}

        <TotalRow
          label="Total"
          value={formatStripeAmount(invoice.total, currency)}
          emphasis
        />
        <TotalRow
          label="Paid"
          value={formatStripeAmount(invoice.amount_paid, currency)}
        />
        <TotalRow
          label="Outstanding"
          value={formatStripeAmount(invoice.amount_remaining, currency)}
          emphasis
        />
        {/* Only meaningful when it happened; a zero row is noise. */}
        {!!invoice.amount_overpaid && (
          <TotalRow
            label="Overpaid"
            value={formatStripeAmount(invoice.amount_overpaid, currency)}
          />
        )}
      </div>
    </div>
  );
};

const TotalRow: React.FC<{
  label: string;
  value: string;
  emphasis?: boolean;
}> = ({ label, value, emphasis }) => (
  <div
    className={cn(
      'flex items-baseline justify-between gap-4',
      emphasis ? 'font-medium' : 'text-muted-foreground',
    )}
  >
    <span>{label}</span>
    <span className="tabular-nums">{value}</span>
  </div>
);

export default InvoiceSummaryList;
